import { WishListCommentElement } from '../interfaces/wish-list-comment.interface';
import { html, nothing, TemplateResult } from 'lit';

export class WishListCommentUtil {
  /**
   * The element interacting with the comment.
   */
  private element: WishListCommentElement;

  /**
   * The identifier for comment elements in the DOM.
   */
  private readonly prefix: string;

  constructor(element: WishListCommentElement, prefix: string) {
    this.element = element;
    this.prefix = prefix;
  }

  /**
   * Renders the collapse toggle button.
   *
   * @param isCommentExpanded
   * @param comment
   * @param disableToggle
   */
  renderCollapseToggleButton(isCommentExpanded: boolean, comment = '', disableToggle = false): TemplateResult {
    return html`
      <button
        id="${this.prefix}-comment-toggle"
        data-automation-id="${this.prefix}-comment-toggle"
        class="btn btn-link p-0"
        data-bs-toggle="collapse"
        data-bs-target="#${this.prefix}-comment"
        aria-expanded="${isCommentExpanded}"
        aria-controls="${this.prefix}-comment"
        @click="${this.element.handleCollapseToggle}"
        ?disabled=${disableToggle}
      >
        ${comment ? 'Edit comment' : 'Add a comment'}
        <uxt-icon
          id="enterDropDownIcon"
          class="ms-1"
          icon="${isCommentExpanded ? 'chevron-up-sm' : 'chevron-down-sm'}"
        ></uxt-icon>
      </button>
    `;
  }

  /**
   * Renders the saved comment value.
   *
   * @param isCommentExpanded
   * @param comment
   */
  renderCommentValue(isCommentExpanded: boolean, comment?: string): TemplateResult | typeof nothing {
    if (!comment || isCommentExpanded) {
      return nothing;
    }

    return html` <div id="${this.prefix}-comment-value">"${comment}"</div>`;
  }

  /**
   * Renders the comment section inside a collapse.
   *
   * @param commentSize
   * @param commentMaxSize
   * @param hasErrors
   * @param placeholder
   * @param disableActions
   */
  renderCommentCollapse(
    commentSize: number,
    commentMaxSize: number,
    hasErrors: boolean,
    placeholder: string,
    disableActions = false
  ): TemplateResult {
    return html`
      <div id="${this.prefix}-comment" class="collapse">
        <textarea
          id="${this.prefix}-input"
          data-automation-id="${this.prefix}-input"
          class="form-control border-2 border-gray rounded ${hasErrors ? 'is-invalid' : ''}"
          maxlength="${commentMaxSize}"
          aria-label="Type to add a comment"
          placeholder="${placeholder}"
          rows="3"
          @input=${this.element.updateCommentFromInput}
        ></textarea>
        <span class="invalid-feedback">
          Allowed characters: alphanumeric, commas, dots, spaces.
        </span>
        <div class="mt-2">
          <button
            id="${this.prefix}-comment-save"
            data-automation-id="${this.prefix}-comment-save"
            type="button"
            class="btn btn-primary me-1"
            aria-label="Save Comment"
            @click=${this.element.saveComment}
            ?disabled=${disableActions}
          >
            Save Comment
          </button>
          <button
            id="${this.prefix}-comment-cancel"
            data-automation-id="${this.prefix}-comment-cancel"
            type="button"
            class="btn btn-outline-primary"
            aria-label="Cancel"
            @click=${this.element.cancelComment}
            ?disabled=${disableActions}
          >
            Cancel
          </button>
          <div id="${this.prefix}-comment-count" class="fs-small text-right text-gray float-end">
            ${commentSize} / ${commentMaxSize}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Updates the text area with the new comment and the comment size.
   */
  clearCommentInput(shadowRoot: ShadowRoot): void {
    const commentElement = this.getCommentElement(shadowRoot);
    if (commentElement) {
      commentElement.value = '';
    }
  }

  /**
   * Gets the comment from the input.
   */
  getCommentElement(shadowRoot: ShadowRoot): HTMLTextAreaElement {
    return shadowRoot?.querySelector(`#${this.prefix}-input`) as HTMLTextAreaElement;
  }

  /**
   * Validates the comment against the allowed characters.
   *
   * @param comment
   */
  isValidComment(comment?: string): boolean {
    if (!comment) {
      return false;
    }

    return /^[a-zA-Z0-9 .,']+$/i.test(comment);
  }
}
